import { Calendar } from "@/components/ui/calendar";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { format, isValid, parse, parseISO, set } from "date-fns";
import { ptBR } from "date-fns/locale";
import { CalendarIcon, Clock } from "lucide-react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import type { FieldValues, Path, UseFormReturn } from "react-hook-form";
import { IMaskInput } from "react-imask";
import { Button } from "./button";
import { ScrollArea, ScrollBar } from "./scroll-area";

interface DateTimeFieldProps<TFieldValues extends FieldValues> {
  form: UseFormReturn<TFieldValues>;
  name: Path<TFieldValues>;
  label: string;
  placeholder?: string;
  className?: string;
}

interface TimeButtonProps {
  field: FieldValues;
  time: number;
  handleTimeChange: (
    field: FieldValues,
    type: "hour" | "minute",
    value: string,
  ) => void;
  type: "hour" | "minute";
  formatString?: boolean;
}

const MemoizedCalendar = React.memo(Calendar);

const TimeButton = React.memo(
  ({ field, time, handleTimeChange, type, formatString }: TimeButtonProps) => {
    let parsedValue: Date | null;
    try {
      parsedValue = field.value ? parseISO(field.value) : null;
    } catch (error) {
      parsedValue = null;
    }

    const isSelected =
      parsedValue && isValid(parsedValue)
        ? type === "hour"
          ? parsedValue.getHours() === time
          : parsedValue.getMinutes() === time
        : false;

    const buttonClasses = cn(
      "sm:w-full shrink-0 aspect-square",
      isSelected && "ring-2 ring-ring ring-offset-2 sm:ring-offset-background",
    );

    return (
      <Button
        key={time}
        size="icon"
        variant={isSelected ? "default" : "ghost"}
        className={buttonClasses}
        aria-pressed={isSelected}
        onClick={() => handleTimeChange(field, type, time.toString())}
      >
        {formatString ? time.toString().padStart(2, "0") : time}
      </Button>
    );
  },
);

const DateTimeField = <TFieldValues extends FieldValues>({
  form,
  name,
  label,
  placeholder = "Selecione uma data",
  className,
}: DateTimeFieldProps<TFieldValues>) => {
  const [datePopoverOpen, setDatePopoverOpen] = useState(false);
  const [timePopoverOpen, setTimePopoverOpen] = useState(false);
  const watchedValue = form.watch(name);
  const [dateInputValue, setDateInputValue] = useState<string>("");
  const [timeInputValue, setTimeInputValue] = useState<string>("");

  useEffect(() => {
    if (typeof watchedValue === "string" && watchedValue) {
      try {
        const parsed = parseISO(watchedValue);
        if (isValid(parsed)) {
          setDateInputValue(format(parsed, "dd/MM/yyyy"));
          setTimeInputValue(format(parsed, "HH:mm"));
          return;
        }
      } catch (error) {
        // ignore invalid values handled via controlled inputs
      }
    }

    if (!watchedValue) {
      setDateInputValue("");
      setTimeInputValue("");
      setTimePopoverOpen(false);
    }
  }, [watchedValue]);

  const handleDateTimeChange = useCallback(
    (field: FieldValues, date: Date | undefined, time: string) => {
      if (date && isValid(date)) {
        const [hours, minutes] = time.split(":");
        const newDate = set(date, {
          hours: Number.parseInt(hours, 10),
          minutes: Number.parseInt(minutes, 10),
          seconds: 0,
          milliseconds: 0,
        });
        const dateTimeString = format(newDate, "yyyy-MM-dd'T'HH:mm:ss");
        field.onChange(dateTimeString);
      } else {
        field.onChange(undefined);
      }
    },
    [],
  );

  const handleDateInputChange = useCallback(
    (field: FieldValues, value: string) => {
      setDateInputValue(value);
      const digitsOnly = value.replace(/\D/g, "");

      if (!value) {
        field.onChange(undefined);
        setTimeInputValue("");
        return;
      }

      if (digitsOnly.length === 8) {
        const parsedDate = parse(value, "dd/MM/yyyy", new Date());
        if (isValid(parsedDate)) {
          let hours = 0;
          let minutes = 0;

          if (/^\d{2}:\d{2}$/.test(timeInputValue)) {
            const [hh, mm] = timeInputValue.split(":");
            hours = Number.parseInt(hh, 10);
            minutes = Number.parseInt(mm, 10);
          } else if (field.value) {
            try {
              const currentValue = parseISO(field.value);
              if (isValid(currentValue)) {
                hours = currentValue.getHours();
                minutes = currentValue.getMinutes();
              }
            } catch (error) {
              // ignore invalid ISO strings while typing
            }
          }

          const newDate = set(parsedDate, {
            hours,
            minutes,
            seconds: 0,
            milliseconds: 0,
          });

          field.onChange(format(newDate, "yyyy-MM-dd'T'HH:mm:ss"));
          setDateInputValue(format(newDate, "dd/MM/yyyy"));
          if (/^\d{2}:\d{2}$/.test(timeInputValue)) {
            setTimeInputValue(format(newDate, "HH:mm"));
          }
        }
      }
    },
    [timeInputValue],
  );

  const handleTimeInputChange = useCallback(
    (field: FieldValues, value: string) => {
      setTimeInputValue(value);
      const digitsOnly = value.replace(/\D/g, "");

      if (!value) {
        if (field.value) {
          try {
            const currentDate = parseISO(field.value);
            if (isValid(currentDate)) {
              const resetDate = set(currentDate, {
                hours: 0,
                minutes: 0,
                seconds: 0,
                milliseconds: 0,
              });
              field.onChange(format(resetDate, "yyyy-MM-dd'T'HH:mm:ss"));
              setTimeInputValue("00:00");
              return;
            }
          } catch (error) {
            field.onChange(undefined);
            return;
          }
        }
        field.onChange(undefined);
        return;
      }

      if (digitsOnly.length === 4 && field.value) {
        try {
          const currentDate = parseISO(field.value);
          if (isValid(currentDate)) {
            const parsedTime = parse(value, "HH:mm", currentDate);
            if (isValid(parsedTime)) {
              const newDate = set(currentDate, {
                hours: parsedTime.getHours(),
                minutes: parsedTime.getMinutes(),
                seconds: 0,
                milliseconds: 0,
              });
              field.onChange(format(newDate, "yyyy-MM-dd'T'HH:mm:ss"));
              setTimeInputValue(format(newDate, "HH:mm"));
            }
          }
        } catch (error) {
          // ignore invalid ISO strings while typing
        }
      }
    },
    [],
  );

  const handleTimeChange = useCallback(
    (field: FieldValues, type: "hour" | "minute", value: string) => {
      if (!field.value) {
        return;
      }

      try {
        const currentDate = parseISO(field.value);
        if (!isValid(currentDate)) {
          return;
        }

        let newDate = currentDate;
        if (type === "hour") {
          newDate = set(currentDate, { hours: Number.parseInt(value, 10) });
        } else {
          newDate = set(currentDate, { minutes: Number.parseInt(value, 10) });
        }

        if (isValid(newDate)) {
          const dateTimeString = format(newDate, "yyyy-MM-dd'T'HH:mm:ss");
          field.onChange(dateTimeString);
          setTimeInputValue(format(newDate, "HH:mm"));
          if (type === "minute") {
            setTimePopoverOpen(false);
          }
        }
      } catch (error) {
        // ignore invalid ISO strings
      }
    },
    [setTimePopoverOpen, setTimeInputValue],
  );

  const hours = useMemo(() => Array.from({ length: 24 }, (_, i) => i), []);
  const minutes = useMemo(
    () => Array.from({ length: 12 }, (_, i) => i * 5),
    [],
  );

  return (
    <FormField
      control={form.control}
      name={name}
      render={({ field }) => {
        const parsedFieldDate = (() => {
          if (!field.value || typeof field.value !== "string") {
            return undefined;
          }
          try {
            const parsedValue = parseISO(field.value);
            return isValid(parsedValue) ? parsedValue : undefined;
          } catch (error) {
            return undefined;
          }
        })();

        const isTimeEnabled = Boolean(parsedFieldDate);
        const timePlaceholder = "Selecione um horário";

        return (
          <FormItem className={cn("flex flex-col", className)}>
            <FormLabel>{label}</FormLabel>
            <div className="flex flex-col gap-2 sm:flex-row">
              <FormControl>
                <Popover
                  open={datePopoverOpen}
                  onOpenChange={setDatePopoverOpen}
                >
                  <div className="flex border rounded-md">
                    <PopoverTrigger asChild>
                      <div className="w-full cursor-pointer">
                        <IMaskInput
                          mask="00/00/0000"
                          value={dateInputValue}
                          onAccept={(value: string) =>
                            handleDateInputChange(field, value)
                          }
                          onBlur={field.onBlur}
                          placeholder={placeholder}
                          className="bg-transparent outline-hidden text-sm flex w-full rounded-md px-3 py-2"
                        />
                      </div>
                    </PopoverTrigger>
                    <Button
                      type="button"
                      variant="ghost"
                      className="rounded-l-none"
                      onClick={() => setDatePopoverOpen(true)}
                    >
                      <CalendarIcon className="h-4 w-4" />
                    </Button>
                  </div>

                  <PopoverContent className="w-auto p-0" align="start">
                    <MemoizedCalendar
                      locale={ptBR}
                      mode="single"
                      selected={parsedFieldDate}
                      onSelect={(selectedDate) => {
                        if (selectedDate) {
                          const fallbackTime = parsedFieldDate
                            ? format(parsedFieldDate, "HH:mm")
                            : "00:00";
                          const currentTime = /^\d{2}:\d{2}$/.test(
                            timeInputValue,
                          )
                            ? timeInputValue
                            : fallbackTime;

                          const [hours, minutes] = currentTime.split(":");
                          const newDate = set(selectedDate, {
                            hours: Number.parseInt(hours, 10),
                            minutes: Number.parseInt(minutes, 10),
                            seconds: 0,
                            milliseconds: 0,
                          });

                          handleDateTimeChange(
                            field,
                            newDate,
                            format(newDate, "HH:mm"),
                          );
                          setDateInputValue(format(newDate, "dd/MM/yyyy"));
                          setTimeInputValue(format(newDate, "HH:mm"));
                          setDatePopoverOpen(false);
                        } else {
                          handleDateTimeChange(field, undefined, "00:00");
                          setDateInputValue("");
                          setTimeInputValue("");
                        }
                      }}
                      autoFocus
                      defaultMonth={parsedFieldDate ?? undefined}
                    />
                  </PopoverContent>
                </Popover>
              </FormControl>

              <FormControl>
                <Popover
                  open={isTimeEnabled ? timePopoverOpen : false}
                  onOpenChange={(open) => {
                    if (isTimeEnabled) {
                      setTimePopoverOpen(open);
                    }
                  }}
                >
                  <div className="flex border rounded-md">
                    <PopoverTrigger asChild>
                      <div
                        className={cn(
                          "w-full",
                          !isTimeEnabled && "cursor-not-allowed",
                        )}
                      >
                        <IMaskInput
                          mask="00:00"
                          value={timeInputValue}
                          onAccept={(value: string) =>
                            handleTimeInputChange(field, value)
                          }
                          onBlur={field.onBlur}
                          disabled={!isTimeEnabled}
                          placeholder={timePlaceholder}
                          className={cn(
                            "bg-transparent outline-hidden text-sm flex w-full rounded-md px-3 py-2",
                            !isTimeEnabled && "text-muted-foreground",
                          )}
                        />
                      </div>
                    </PopoverTrigger>
                    <Button
                      type="button"
                      variant="ghost"
                      className="rounded-l-none"
                      onClick={() => isTimeEnabled && setTimePopoverOpen(true)}
                      disabled={!isTimeEnabled}
                    >
                      <Clock className="h-4 w-4" />
                    </Button>
                  </div>

                  <PopoverContent className="w-auto p-0" align="start">
                    <div className="sm:flex">
                      <div className="flex flex-col sm:flex-row sm:h-[300px] divide-y sm:divide-y-0 sm:divide-x">
                        <ScrollArea className="w-64 sm:w-auto">
                          <div className="flex sm:flex-col p-2">
                            {[...hours].reverse().map((hour) => (
                              <TimeButton
                                key={hour}
                                time={hour}
                                field={field}
                                handleTimeChange={handleTimeChange}
                                type="hour"
                              />
                            ))}
                          </div>
                          <ScrollBar
                            orientation="horizontal"
                            className="sm:hidden"
                          />
                        </ScrollArea>
                        <ScrollArea className="w-64 sm:w-auto">
                          <div className="flex sm:flex-col p-2">
                            {minutes.map((minute) => (
                              <TimeButton
                                key={minute}
                                time={minute}
                                field={field}
                                handleTimeChange={handleTimeChange}
                                type="minute"
                                formatString={true}
                              />
                            ))}
                          </div>
                          <ScrollBar
                            orientation="horizontal"
                            className="sm:hidden"
                          />
                        </ScrollArea>
                      </div>
                    </div>
                  </PopoverContent>
                </Popover>
              </FormControl>
            </div>
            <FormMessage />
          </FormItem>
        );
      }}
    />
  );
};

export default DateTimeField;
